import { Drawer, Loading } from "@components";
import ComboFilters from "pages/Reports/ComboFilters";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import classNames from "classnames/bind";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { ReportsAPI } from "providers/ReportsAPI";
import { If } from "shared/components/control-statements";
const css = classNames.bind(styles);
const buildTable = (data) => {
  const statuses = {
    novos: 'ERA "NOVOS"',
    onboarding: 'ERA "ONBOARDING"',
    engajados: 'ERA "ENGAJADOS"',
    atrasados: 'ERA "ATRASADOS"',
  };

  const rows = ["novos", "onboarding", "engajados", "atrasados"];
  const columns = ["onboarding", "engajados", "atrasados"];

  const table = rows.map((row) => {
    return {
      alias: row,
      status: statuses[row],
      values: columns.map((column) => {
        const key = `${row}__${column}`;
        return {
          label: `VIROU ${statuses[column].replace("ERA ", "")}`,
          tuple: `${row}__${column}`,
          value: data?.[key] ?? 0,
        };
      }),
    };
  });

  return table;
};

export default function StageTransitions() {
  const getShade = (value, max) => {
    if (!value || +value === 0) {
      return {
        background: "#1e1e1f",
        color: "#808080",
      };
    }

    const intensity = Math.min(Math.max(value / max, 0), 1);
    const alpha = 0.6 + intensity * 1;

    return {
      background: `rgba(97, 97, 255, ${intensity})`,
      color: `rgba(255, 255, 255, ${alpha})`,
    };
  };

  const months = getAllMonthsUntilNow(false);

  const [hovered, setHovered] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("quantity");
  const [loadingHistoric, setLoadingHistoric] = useState(false);

  const provider = new ReportsAPI();

  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(months[0]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const fetchHistory = async ({ year, month }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.lifeCycleHistory("transitions", year, month);
    const rows = Array(12).fill(0);

    results.forEach((result, index) => {
      const filteredResult = result[selectedFilter];
      rows[index] = filteredResult?.[selectedData.alias] || 0;
    });

    setLoadingHistoric(false);
    return rows;
  };

  const fetchData = async () => {
    setLoading(true);
    const response = await provider.lifeCycle(
      "transitions",
      selectedMonth.year,
      selectedMonth.month
    );
    setRawData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const getAfterDoubleUnderscore = (str) => {
    if (!str) return null;
    const parts = str.split("__");
    return parts.length > 1 ? parts[1] : null;
  };

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Transições entre estágios</p>
          <span
            className={styles.info}
            title="Quantidade de clientes que transicionam de um estágio."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>

        <div className="d-flex align-center">
          <div className="d-flex align-center gap-10">
            <div
              className={css({
                selectMini: true,
                selected: selectedFilter === "quantity",
              })}
              onClick={() => setSelectedFilter("quantity")}
            >
              Qtd. clientes
            </div>
            <div
              className={css({
                selectMini: true,
                selected: selectedFilter === "time",
              })}
              onClick={() => setSelectedFilter("time")}
            >
              Tempo de transição
            </div>
          </div>
          <div className={styles.divider} />
          <div className="d-flex align-center">
            <ComboFilters
              options={months}
              onSelect={(option) => setSelectedMonth(option)}
              selectedOption={selectedMonth}
            />
          </div>
        </div>
      </div>

      <div className={styles.hairline} />

      {loading ? (
        <div
          className="d-flex align-center justify-center"
          style={{ height: 192 }}
        >
          <Loading />
        </div>
      ) : (
        <div
          className={styles.tableTransitions}
          onMouseDown={onMouseDown}
          ref={scrollRef}
        >
          <table>
            <thead>
              <tr>
                <th />
                <th>VIROU "ONBOARDING"</th>
                <th>VIROU "ENGAJADOS"</th>
                <th>VIROU "ATRASADOS"</th>
              </tr>
            </thead>
            <tbody>
              {buildTable(rawData?.[selectedFilter]).map((row) => (
                <tr key={row.status}>
                  <td
                    className={styles.status}
                    style={{ maxWidth: 200, width: 200 }}
                  >
                    {row.status}
                  </td>
                  {row.values.map(({ tuple, label, value }, idx) => {
                    const allValues = buildTable(
                      rawData?.[selectedFilter]
                    ).flatMap((item) => item.values);

                    const sameColunRow = (str) => {
                      const [before, after] = str.split("__");
                      return before === after;
                    };

                    const maxItem = allValues
                      .filter((item) => !sameColunRow(item.tuple))
                      .reduce((max, item) => {
                        return Number(item.value) > Number(max.value)
                          ? item
                          : max;
                      });

                    let style;

                    if (sameColunRow(tuple)) {
                      style = {
                        background: "transparent",
                        color: "#808080",
                      };
                    } else style = getShade(value, Number(maxItem.value));

                    return (
                      <td
                        key={idx}
                        style={{
                          backgroundColor: style.background,
                          color: style.color,
                          ...(value && { cursor: "pointer" }),
                          ...(value &&
                            value === hovered?.alias && {
                              filter: "brightness(1.1)",
                            }),
                        }}
                        onMouseEnter={() =>
                          setHovered({
                            alias: `${tuple}`,
                            name: `${row.status}`,
                          })
                        }
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                          if (value && !sameColunRow(tuple))
                            setSelectedData({
                              alias: `${tuple}`,
                              name: `${row.status} E ${label}`,
                            });
                        }}
                      >
                        <If cond={sameColunRow(tuple)}>---</If>
                        <If cond={!sameColunRow(tuple)}>
                          {selectedFilter === "quantity"
                            ? value ?? "---"
                            : value
                            ? `${value} dias`
                            : "---"}
                        </If>

                        <div
                          className={styles.chart}
                          style={{
                            ...(value &&
                              tuple === hovered?.alias && {
                                transform: "translateY(0)",
                                opacity: 1,
                              }),
                          }}
                        >
                          <i className="far fa-chart-waterfall" />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          enableMonth={false}
          onClose={() => setSelectedData(false)}
          loading={loadingHistoric}
          onFetchData={(...params) => fetchHistory(...params)}
          startOn2025
          invertColorSign={
            getAfterDoubleUnderscore(selectedData?.alias) === "atrasados"
          }
        />
      </Drawer>
    </div>
  );
}
